import type { FilterId } from '../../../filters/types/filters';
import type { ReportParameterConfig } from '../../PageConfigProvider/page-reducer';
import type {
  FieldBindingMode,
  SnowflakeConfigField,
} from '../../PageConfigProvider/types/item';

function findMatchingParameter(
  field: SnowflakeConfigField,
  reportParameters: ReportParameterConfig[]
): ReportParameterConfig | undefined {
  const compatibleIds = getCompatibleFilterIdsForField(field);
  if (compatibleIds.length === 0) return undefined;
  return reportParameters.find((p) => compatibleIds.includes(p.filterId));
}

/**
 * Filter compatibility is declared on each saved-query field (`compatibleFilterIds` from the API)
 * and copied onto `SnowflakeConfigField` when the chart loads a query — no UI heuristics.
 */
export function getCompatibleFilterIdsForField(
  field: SnowflakeConfigField
): FilterId[] {
  return field.compatibleFilterIds ?? [];
}

/**
 * Filters reportParameters to only those compatible with a given field.
 * Falls back to returning all parameters if no compatibility rule is known.
 */
export function getCompatibleParameters(
  field: SnowflakeConfigField,
  reportParameters: ReportParameterConfig[]
): ReportParameterConfig[] {
  const compatibleIds = getCompatibleFilterIdsForField(field);
  if (compatibleIds.length === 0) return reportParameters;
  return reportParameters.filter((p) => compatibleIds.includes(p.filterId));
}

/**
 * Auto-binds snowflake fields to report parameters using `compatibleFilterIds` from saved-query metadata.
 *
 * Fields that already have a `parameterId` or `bindingMode` set are left unchanged.
 * Fields that can't be auto-matched default to `bindingMode: 'static'`.
 */
export function autoMapFieldsToFilters(
  fields: SnowflakeConfigField[],
  reportParameters: ReportParameterConfig[]
): SnowflakeConfigField[] {
  if (reportParameters.length === 0) {
    return fields.map((f) => ({
      ...f,
      bindingMode: inferBindingMode(f),
    }));
  }

  return fields.map((f) => {
    if (f.bindingMode !== undefined) return f;

    if (f.parameterId !== undefined && f.parameterId !== '') {
      return { ...f, bindingMode: 'report_parameter' as FieldBindingMode };
    }

    const match = findMatchingParameter(f, reportParameters);
    if (match) {
      return {
        ...f,
        bindingMode: 'report_parameter' as FieldBindingMode,
        parameterId: match.parameterId,
        value: '',
      };
    }

    return { ...f, bindingMode: 'static' as FieldBindingMode };
  });
}

function inferBindingMode(field: SnowflakeConfigField): FieldBindingMode {
  if (field.parameterId !== undefined && field.parameterId !== '') {
    return 'report_parameter';
  }
  if (field.boundFilterId !== undefined && field.boundFilterId !== '') {
    return 'dashboard_filter';
  }
  return 'static';
}

/**
 * Returns a human-readable summary of a field's binding state,
 * suitable for display in parameter cards.
 */
export function describeFieldBinding(
  field: SnowflakeConfigField,
  reportParameters: ReportParameterConfig[]
): { mode: FieldBindingMode; label: string } {
  const mode = field.bindingMode ?? inferBindingMode(field);

  if (mode === 'report_parameter' && field.parameterId) {
    const param = reportParameters.find(
      (p) => p.parameterId === field.parameterId
    );
    return {
      mode,
      label: param ? `Linked to: ${param.name}` : 'Linked to parameter',
    };
  }

  if (mode === 'dashboard_filter' && field.boundFilterId) {
    return {
      mode,
      label: `Linked to filter: ${field.boundFilterId}`,
    };
  }

  const displayValue = field.value?.trim();
  return {
    mode: 'static',
    label: displayValue ? `Static: ${displayValue}` : 'Not set',
  };
}
